import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Check, X, Clock, FileText, PenLine, Trash2, Info, SendHorizontal, CreditCard } from 'lucide-react';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import {
  purchaseOrderService,
  PurchaseOrderDTO,
  PurchaseOrderStatus,
  ApprovalStatus,
  CancellationStatus,
  PurchaseOrderApprovalStatusDTO,
  CancellationRequestDTO,
  PurchaseOrderPaymentDTO,
  RecordPurchaseOrderPaymentRequest,
} from '../../services/purchaseOrderService';
import paymentTypeService, { PaymentType } from '../../services/paymentTypeService';
import { parseApiError } from '../../utils/errorUtils';
import './PurchaseOrderPage.css';

// ─── Badge helpers ─────────────────────────────────────────────────────────────

const STATUS_BADGE: Record<PurchaseOrderStatus, string> = {
  Draft: 'ims-badge ims-badge-draft',
  Submitted: 'ims-badge ims-badge-submitted',
  Confirmed: 'ims-badge ims-badge-confirmed',
  PartiallyReceived: 'ims-badge ims-badge-partially-received',
  Received: 'ims-badge ims-badge-received',
  Closed: 'ims-badge ims-badge-closed',
};

const STATUS_I18N: Record<PurchaseOrderStatus, string> = {
  Draft: 'purchaseOrder.statusDraft',
  Submitted: 'purchaseOrder.statusSubmitted',
  Confirmed: 'purchaseOrder.statusConfirmed',
  PartiallyReceived: 'purchaseOrder.statusPartiallyReceived',
  Received: 'purchaseOrder.statusReceived',
  Closed: 'purchaseOrder.statusClosed',
};

const APPROVAL_BADGE: Record<ApprovalStatus, string> = {
  Draft: 'ims-badge ims-badge-draft',
  PendingApproval: 'ims-badge ims-badge-pending',
  Approved: 'ims-badge ims-badge-approved',
  Rejected: 'ims-badge ims-badge-rejected',
};

const APPROVAL_RECORD_BADGE: Record<string, string> = {
  Pending: 'ims-badge ims-badge-pending',
  Approved: 'ims-badge ims-badge-approved',
  Rejected: 'ims-badge ims-badge-rejected',
};

const CANCELLATION_BADGE: Record<CancellationStatus, string> = {
  None: 'ims-badge ims-badge-draft',
  PendingCancellation: 'ims-badge ims-badge-pending',
  Cancelled: 'ims-badge ims-badge-rejected',
  CancellationRejected: 'ims-badge ims-badge-draft',
};

const CANCELLABLE_STATUSES: PurchaseOrderStatus[] = ['Draft', 'Submitted', 'PartiallyReceived'];

// ─── Approval Level Row (per-level, inline buttons) ──────────────────────────

interface POApprovalLevelRowProps {
  approval: PurchaseOrderApprovalDTO;
  currentUserId: number;
  onApprove: (id: number) => void;
  onReject: (id: number) => void;
  busy: boolean;
  blocked: boolean;
  t: (key: string) => string;
}

const POApprovalLevelRow: React.FC<POApprovalLevelRowProps> = ({
  approval, currentUserId, onApprove, onReject, busy, blocked, t,
}) => {
  const isAssigned = approval.assignedToUserId === currentUserId;

  const statusIcon = ({
    Approved: (
      <span className="grn-level-icon grn-level-icon--approved">
        <Check size={14} strokeWidth={2.5} />
      </span>
    ),
    Rejected: (
      <span className="grn-level-icon grn-level-icon--rejected">
        <X size={14} strokeWidth={2} />
      </span>
    ),
    Pending: (
      <span className="grn-level-icon grn-level-icon--pending">
        <Clock size={14} strokeWidth={2} />
      </span>
    ),
  } as Record<string, JSX.Element>)[approval.status];

  return (
    <div className={`grn-level-row ${approval.status === 'Pending' && isAssigned ? 'grn-level-row--mine' : ''}`}>
      <div className="grn-level-left">
        {statusIcon}
        <div>
          <div className="grn-level-title">
            {t('purchaseOrder.approval.level')} {approval.approvalLevel}
            {isAssigned && approval.status === 'Pending' && (
              <span className="grn-level-mine-badge">{t('purchaseOrder.approval.yourApproval')}</span>
            )}
          </div>
          <div className="grn-level-sub">
            {approval.status === 'Approved' && approval.approvingUserName && (
              <>{t('purchaseOrder.approval.approvedBy')} <strong>{approval.approvingUserName}</strong>
                {approval.approvedAt && ` · ${new Date(approval.approvedAt).toLocaleDateString()}`}
              </>
            )}
            {approval.status === 'Rejected' && (
              <span style={{ color: 'var(--ims-red-text)' }}>
                {t('purchaseOrder.approval.rejectedBy')} {approval.approvingUserName ?? '—'}
                {approval.rejectionReason && ` — ${approval.rejectionReason}`}
              </span>
            )}
            {approval.status === 'Pending' && (
              <>{t('purchaseOrder.approval.assignedTo')} <strong>{approval.assignedToUserName ?? `#${approval.assignedToUserId}`}</strong></>
            )}
            {approval.approvalComments && (
              <div style={{ marginTop: 2, color: 'var(--ims-text-sub)', fontStyle: 'italic' }}>
                "{approval.approvalComments}"
              </div>
            )}
          </div>
        </div>
      </div>
      {approval.status === 'Pending' && isAssigned && (
        <div className="grn-level-actions">
          {blocked ? (
            <span style={{ fontSize: 12, color: 'var(--ims-amber-text)', fontWeight: 500 }}>
              {t('purchaseOrder.approval.awaitingPriorLevel')}
            </span>
          ) : (
            <>
              <button
                className="ims-btn ims-btn-success"
                style={{ fontSize: 12, padding: '4px 10px' }}
                disabled={busy}
                onClick={() => onApprove(approval.id)}
              >
                {t('purchaseOrder.approval.approve')}
              </button>
              <button
                className="btn-delete"
                style={{ fontSize: 12, padding: '4px 10px' }}
                disabled={busy}
                onClick={() => onReject(approval.id)}
              >
                {t('purchaseOrder.approval.reject')}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Approval Panel ────────────────────────────────────────────────────────────

interface ApprovalPanelProps {
  approvalStatus: PurchaseOrderApprovalStatusDTO;
  currentUserId: number;
  onApprove: (approvalId: number) => void;
  onReject: (approvalId: number) => void;
  busy: boolean;
  t: (key: string) => string;
}

const ApprovalPanel: React.FC<ApprovalPanelProps> = ({
  approvalStatus, currentUserId, onApprove, onReject, busy, t,
}) => (
  <div className="ims-card">
    <div className="ims-card-header">
      <h2 className="ims-card-title">{t('purchaseOrder.approval.approvalStatus')}</h2>
      {approvalStatus.totalApprovalLevels > 0 && (
        <span style={{
          fontSize: 12, fontWeight: 500, color: 'var(--ims-text-sub)',
          background: 'var(--ims-bg-row-alt)', padding: '2px 8px', borderRadius: 20,
        }}>
          {approvalStatus.currentApprovalLevel}/{approvalStatus.totalApprovalLevels} {t('purchaseOrder.approval.levelsCompleted')}
        </span>
      )}
    </div>
    <div className="ims-card-body" style={{ padding: '12px 20px' }}>
      <div className="grn-levels-list">
        {approvalStatus.approvals.map(a => {
          const priorPending = approvalStatus.approvals.some(
            x => x.approvalLevel < a.approvalLevel && x.status !== 'Approved'
          );
          return (
            <POApprovalLevelRow
              key={a.id}
              approval={a}
              currentUserId={currentUserId}
              busy={busy}
              blocked={priorPending}
              onApprove={onApprove}
              onReject={onReject}
              t={t}
            />
          );
        })}
      </div>
    </div>
  </div>
);

// ─── Cancellation Panel ────────────────────────────────────────────────────────

interface CancellationPanelProps {
  order: PurchaseOrderDTO;
  cancellation: CancellationRequestDTO | null;
  currentUserId: number;
  submitting: boolean;
  onRequest: (reason: string) => Promise<void>;
  onWithdraw: () => Promise<void>;
  onApprove: (approvalId: number, comments: string) => Promise<void>;
  onReject: (approvalId: number, reason: string) => Promise<void>;
  t: (key: string) => string;
}

const CancellationPanel: React.FC<CancellationPanelProps> = ({
  order,
  cancellation,
  currentUserId,
  submitting,
  onRequest,
  onWithdraw,
  onApprove,
  onReject,
  t,
}) => {
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);

  const [actionApprovalId, setActionApprovalId] = useState<number | null>(null);
  const [actionType, setActionType] = useState<'approve' | 'reject' | null>(null);
  const [comments, setComments] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const canRequestCancellation =
    CANCELLABLE_STATUSES.includes(order.status) &&
    order.cancellationStatus === 'None';

  const canRequestAgain =
    CANCELLABLE_STATUSES.includes(order.status) &&
    order.cancellationStatus === 'CancellationRejected';

  const myPendingApproval = cancellation?.approvals.find(
    (a) => a.assignedToUserId === currentUserId && a.status === 'Pending',
  );

  const isOriginalRequester = cancellation?.requestedByUserId === currentUserId;

  const handleRequestSubmit = async () => {
    setReasonError(null);
    if (!cancellationReason.trim() || cancellationReason.trim().length < 10) {
      setReasonError(t('purchaseOrder.cancellation.reasonMinLength'));
      return;
    }
    await onRequest(cancellationReason.trim());
    setShowRequestForm(false);
    setCancellationReason('');
  };

  const startAction = (id: number, type: 'approve' | 'reject') => {
    setActionApprovalId(id);
    setActionType(type);
    setFormError(null);
    setComments('');
    setRejectionReason('');
  };

  const handleActionSubmit = async () => {
    setFormError(null);
    if (!actionApprovalId || !actionType) return;
    if (actionType === 'reject') {
      if (!rejectionReason.trim() || rejectionReason.trim().length < 10) {
        setFormError(t('purchaseOrder.cancellation.rejectionReasonMinLength'));
        return;
      }
      await onReject(actionApprovalId, rejectionReason.trim());
    } else {
      await onApprove(actionApprovalId, comments.trim());
    }
    setActionApprovalId(null);
    setActionType(null);
    setComments('');
    setRejectionReason('');
  };

  return (
    <div className="ims-card">
      <div className="ims-card-header">
        <h2 className="ims-card-title">{t('purchaseOrder.cancellation.title')}</h2>
        {order.cancellationStatus !== 'None' && (
          <span className={CANCELLATION_BADGE[order.cancellationStatus]} style={{ fontSize: 12 }}>
            {t(`purchaseOrder.cancellation.status${order.cancellationStatus}`)}
          </span>
        )}
      </div>
      <div className="ims-card-body">

        {/* Request cancellation form */}
        {(canRequestCancellation || canRequestAgain) && !showRequestForm && (
          <div>
            {canRequestAgain && (
              <div className="ims-alert ims-alert-warning" style={{ fontSize: 13 }}>
                {t('purchaseOrder.cancellation.previouslyRejected')}
                {cancellation?.rejectionNotes && (
                  <> — <em>"{cancellation.rejectionNotes}"</em></>
                )}
              </div>
            )}
            <button
              className="btn-delete"
              disabled={submitting}
              onClick={() => setShowRequestForm(true)}
            >
              {t('purchaseOrder.cancellation.requestCancellation')}
            </button>
          </div>
        )}

        {(canRequestCancellation || canRequestAgain) && showRequestForm && (
          <div className="po-action-form" style={{ background: 'var(--ims-red-bg)', borderColor: '#fecaca' }}>
            <p className="po-action-form-title">{t('purchaseOrder.cancellation.provideReason')}</p>
            <div style={{ marginTop: 8 }}>
              <textarea
                className="ims-form-control ims-textarea"
                rows={4}
                maxLength={2000}
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                placeholder={t('purchaseOrder.cancellation.reasonPlaceholder')}
              />
              {reasonError && (
                <div className="po-action-form-error">{reasonError}</div>
              )}
            </div>
            <div className="po-action-form-footer">
              <button
                className="btn-delete"
                disabled={submitting}
                onClick={handleRequestSubmit}
              >
                {submitting ? <span className="ims-btn-spinner" /> : null}
                {t('purchaseOrder.cancellation.submitRequest')}
              </button>
              <button
                className="btn-cancel"
                disabled={submitting}
                onClick={() => { setShowRequestForm(false); setCancellationReason(''); setReasonError(null); }}
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        )}

        {/* Pending cancellation details */}
        {cancellation && order.cancellationStatus === 'PendingCancellation' && (
          <div>
            <div className="po-cancellation-meta" style={{ marginBottom: 12 }}>
              <strong>{t('purchaseOrder.cancellation.requestedBy')}:</strong>{' '}
              {cancellation.requestedByUserName ?? `User #${cancellation.requestedByUserId}`}
              {' · '}
              <strong>{t('purchaseOrder.cancellation.requestedOn')}:</strong>{' '}
              {new Date(cancellation.createdAt).toLocaleString()}
            </div>
            <div className="po-cancel-reason-box">
              <strong>{t('purchaseOrder.cancellation.reason')}:</strong>{' '}
              {cancellation.cancellationReason}
            </div>

            {/* Approval steps */}
            {cancellation.approvals.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                {cancellation.approvals.map((a) => (
                  <div
                    key={a.id}
                    className={`po-approval-step po-approval-step--${a.status.toLowerCase()}`}
                  >
                    <div className="po-approval-step-dot">
                      {a.approvalLevel}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: 600, fontSize: 13 }}>
                          {t('purchaseOrder.approval.level')} {a.approvalLevel}{' — '}
                          <span style={{ fontWeight: 400 }}>{a.assignedToUserName ?? `User #${a.assignedToUserId}`}</span>
                        </span>
                        <span className={APPROVAL_RECORD_BADGE[a.status] ?? 'ims-badge ims-badge-draft'} style={{ fontSize: 11 }}>
                          {t(`purchaseOrder.approval.record${a.status}`)}
                        </span>
                      </div>
                      {a.actionedAt && (
                        <div className="po-meta-text">
                          {a.approvingUserName && <span>{t('purchaseOrder.approval.actionBy')} {a.approvingUserName} · </span>}
                          {new Date(a.actionedAt).toLocaleString()}
                        </div>
                      )}
                      {a.approvalComments && (
                        <div className="po-comment-text">"{a.approvalComments}"</div>
                      )}
                      {a.rejectionReason && (
                        <div className="po-rejection-text">
                          <strong>{t('purchaseOrder.approval.reason')}:</strong> {a.rejectionReason}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Action buttons for assigned approver */}
            {myPendingApproval && actionApprovalId === null && (
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button className="ims-btn ims-btn-success" disabled={submitting} onClick={() => startAction(myPendingApproval.id, 'approve')}>
                  ✓ {t('purchaseOrder.cancellation.approve')}
                </button>
                <button className="btn-delete" disabled={submitting} onClick={() => startAction(myPendingApproval.id, 'reject')}>
                  ✕ {t('purchaseOrder.cancellation.reject')}
                </button>
              </div>
            )}

            {/* Inline action form */}
            {actionApprovalId !== null && actionType !== null && (
              <div className="po-action-form">
                <p className="po-action-form-title">
                  {actionType === 'approve'
                    ? t('purchaseOrder.cancellation.confirmApprove')
                    : t('purchaseOrder.cancellation.confirmReject')}
                </p>
                {actionType === 'approve' ? (
                  <div style={{ marginTop: 8 }}>
                    <label className="po-action-form-label">
                      {t('purchaseOrder.approval.comments')} ({t('common.optional')})
                    </label>
                    <textarea
                      className="ims-form-control ims-textarea" rows={2} maxLength={1000}
                      value={comments} onChange={(e) => setComments(e.target.value)}
                      placeholder={t('purchaseOrder.approval.commentsPlaceholder')}
                    />
                  </div>
                ) : (
                  <div style={{ marginTop: 8 }}>
                    <label className="po-action-form-label">
                      {t('purchaseOrder.cancellation.rejectionReason')} *
                    </label>
                    <textarea
                      className="ims-form-control ims-textarea" rows={3} maxLength={2000}
                      value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)}
                      placeholder={t('purchaseOrder.cancellation.rejectionReasonPlaceholder')}
                    />
                    {formError && <div className="po-action-form-error">{formError}</div>}
                  </div>
                )}
                <div className="po-action-form-footer">
                  <button
                    className={`ims-btn ${actionType === 'approve' ? 'ims-btn-success' : 'ims-btn-danger'}`}
                    disabled={submitting} onClick={handleActionSubmit}
                  >
                    {submitting ? <span className="ims-btn-spinner" /> : null}
                    {t('common.confirm')}
                  </button>
                  <button
                    className="btn-cancel"
                    disabled={submitting}
                    onClick={() => { setActionApprovalId(null); setActionType(null); setFormError(null); }}
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              </div>
            )}

            {/* Withdraw button */}
            {isOriginalRequester && actionApprovalId === null && (
              <div className="po-withdraw-section">
                <button
                  className="ims-btn ims-btn-secondary"
                  disabled={submitting}
                  onClick={onWithdraw}
                >
                  {t('purchaseOrder.cancellation.withdrawRequest')}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Completed cancellation */}
        {order.cancellationStatus === 'Cancelled' && cancellation && (
          <div className="po-cancellation-meta">
            <div className="ims-alert ims-alert-error" style={{ fontSize: 13 }}>
              {t('purchaseOrder.cancellation.cancelledMessage')}
              {cancellation.cancelledAt && (
                <> — {new Date(cancellation.cancelledAt).toLocaleString()}</>
              )}
            </div>
            <div>
              <strong>{t('purchaseOrder.cancellation.reason')}:</strong> {cancellation.cancellationReason}
            </div>
          </div>
        )}

        {/* No cancellation activity */}
        {!cancellation && order.cancellationStatus === 'None' && !canRequestCancellation && (
          <p className="po-not-eligible-text">
            {t('purchaseOrder.cancellation.notEligible')}
          </p>
        )}
      </div>
    </div>
  );
};

// ─── Main page ─────────────────────────────────────────────────────────────────

const PurchaseOrderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [order, setOrder] = useState<PurchaseOrderDTO | null>(null);
  const [approvalStatus, setApprovalStatus] = useState<PurchaseOrderApprovalStatusDTO | null>(null);
  const [cancellation, setCancellation] = useState<CancellationRequestDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(
    (location.state as { errorMsg?: string } | null)?.errorMsg ?? null
  );
  const [submitting, setSubmitting] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Approval modal state
  const [showApproveModal,    setShowApproveModal]    = useState(false);
  const [approvingApprovalId, setApprovingApprovalId] = useState<number | null>(null);
  const [approvalComments,    setApprovalComments]    = useState('');
  const [showRejectModal,     setShowRejectModal]     = useState(false);
  const [rejectingApprovalId, setRejectingApprovalId] = useState<number | null>(null);
  const [rejectionReason,     setRejectionReason]     = useState('');

  // Payment state
  const [payments, setPayments] = useState<PurchaseOrderPaymentDTO[]>([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [activePaymentTypes, setActivePaymentTypes] = useState<PaymentType[]>([]);
  const [paymentData, setPaymentData] = useState<RecordPurchaseOrderPaymentRequest>({
    amount: 0,
    paymentDate: new Date().toISOString().slice(0, 10),
    paymentMethod: '',
    referenceNumber: '',
    notes: '',
  });

  const currentUserId = user?.id ?? 0;

  const loadPayments = useCallback(async () => {
    if (!id) return;
    setPaymentsLoading(true);
    try {
      const res = await purchaseOrderService.getPayments(id);
      const data = res.data?.data ?? res.data ?? [];
      setPayments(Array.isArray(data) ? data : []);
    } catch {
      // Non-critical
    } finally {
      setPaymentsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadOrder();
    loadPayments();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load active payment types once for the Record Payment dropdown
  useEffect(() => {
    paymentTypeService.getActivePaymentTypes('Purchase')
      .then((types) => {
        if (Array.isArray(types) && types.length > 0) {
          setActivePaymentTypes(types);
          // Pre-select the default type (excluding Credit — PO payments are real money transfers)
          const def = types.find((pt) => pt.isDefault && pt.paymentCategory !== 'Credit')
            ?? types.find((pt) => pt.paymentCategory !== 'Credit')
            ?? types[0];
          setPaymentData((prev) => ({ ...prev, paymentMethod: def?.code ?? '' }));
        }
      })
      .catch(() => {});
  }, []);

  const loadOrder = async () => {
    setLoading(true);
    setError(null);
    try {
      // Use allSettled so a failing approval-status call never prevents the PO from loading.
      const [orderResult, approvalResult] = await Promise.allSettled([
        purchaseOrderService.getPurchaseOrderById(id || '0'),
        purchaseOrderService.getApprovalStatus(id || '0'),
      ]);

      if (orderResult.status === 'rejected') {
        setError(t('purchaseOrder.failedToLoadOrder'));
        return;
      }

      const loadedOrder = orderResult.value?.data?.data ?? null;
      if (loadedOrder) setOrder(loadedOrder);

      if (approvalResult.status === 'fulfilled' && approvalResult.value?.data?.data) {
        setApprovalStatus(approvalResult.value.data.data);
      }
      // If approval status fails, the approval panel just won't render — not a fatal error.

      // Load cancellation status if any cancellation activity exists
      if (loadedOrder && loadedOrder.cancellationStatus !== 'None') {
        try {
          const cancelRes = await purchaseOrderService.getCancellationStatus(id || '0');
          if (cancelRes?.data?.data) setCancellation(cancelRes.data.data);
        } catch {
          // Not critical — cancellation panel will just show the empty state
        }
      }
    } catch {
      setError(t('purchaseOrder.failedToLoadOrder'));
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (approvalId: number) => {
    setSubmitting(true); setError(null);
    try {
      const res = await purchaseOrderService.approvePurchaseOrder(id || '0', approvalId, { approvalComments: approvalComments || undefined });
      if (res?.data?.data) setApprovalStatus(res.data.data);
      const updated = await purchaseOrderService.getPurchaseOrderById(id || '0');
      if (updated?.data?.data) setOrder(updated.data.data);
      setShowApproveModal(false); setApprovalComments(''); setApprovingApprovalId(null);
      setSuccessMsg(t('purchaseOrder.approval.approvedSuccess'));
      setTimeout(() => setSuccessMsg(null), 4500);
    } catch (e: unknown) {
      setError(parseApiError(e, t('purchaseOrder.approval.failedToApprove')).message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!rejectingApprovalId) return;
    if (!rejectionReason.trim()) { setError(t('purchaseOrder.approval.rejectionReasonRequired')); return; }
    setSubmitting(true); setError(null);
    try {
      const res = await purchaseOrderService.rejectPurchaseOrder(id || '0', rejectingApprovalId, { rejectionReason });
      if (res?.data?.data) setApprovalStatus(res.data.data);
      const updated = await purchaseOrderService.getPurchaseOrderById(id || '0');
      if (updated?.data?.data) setOrder(updated.data.data);
      setShowRejectModal(false); setRejectionReason(''); setRejectingApprovalId(null);
      setSuccessMsg(t('purchaseOrder.approval.rejectedSuccess'));
      setTimeout(() => setSuccessMsg(null), 4500);
    } catch (e: unknown) {
      setError(parseApiError(e, t('purchaseOrder.approval.failedToReject')).message);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Cancellation handlers ──────────────────────────────────────────────────

  const handleRequestCancellation = async (reason: string) => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await purchaseOrderService.requestCancellation(id || '0', { cancellationReason: reason });
      if (res?.data?.data) setCancellation(res.data.data);
      setSuccessMsg(t('purchaseOrder.cancellation.requestedSuccess'));
      setTimeout(() => setSuccessMsg(null), 4000);
      await loadOrder();
    } catch (err: unknown) {
      setError(parseApiError(err, t('purchaseOrder.cancellation.failedToRequest')).message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleWithdrawCancellation = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await purchaseOrderService.withdrawCancellation(id || '0');
      if (res?.data?.data) setCancellation(res.data.data);
      setSuccessMsg(t('purchaseOrder.cancellation.withdrawnSuccess'));
      setTimeout(() => setSuccessMsg(null), 4000);
      await loadOrder();
    } catch {
      setError(t('purchaseOrder.cancellation.failedToWithdraw'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproveCancellation = async (approvalId: number, comments: string) => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await purchaseOrderService.approveCancellation(approvalId, { approvalComments: comments || undefined });
      if (res?.data?.data) setCancellation(res.data.data);
      setSuccessMsg(t('purchaseOrder.cancellation.approvedSuccess'));
      setTimeout(() => setSuccessMsg(null), 4000);
      await loadOrder();
    } catch {
      setError(t('purchaseOrder.cancellation.failedToApprove'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRejectCancellation = async (approvalId: number, reason: string) => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await purchaseOrderService.rejectCancellation(approvalId, { rejectionReason: reason });
      if (res?.data?.data) setCancellation(res.data.data);
      setSuccessMsg(t('purchaseOrder.cancellation.rejectedSuccess'));
      setTimeout(() => setSuccessMsg(null), 4000);
      await loadOrder();
    } catch {
      setError(t('purchaseOrder.cancellation.failedToReject'));
    } finally {
      setSubmitting(false);
    }
  };

  // ── Payment handler ───────────────────────────────────────────────────────

  const handleRecordPayment = async () => {
    if (!order) return;
    if (!paymentData.amount || paymentData.amount <= 0) {
      setPaymentError(t('purchaseOrder.payment.amountRequired'));
      return;
    }
    setPaymentSubmitting(true);
    setPaymentError(null);
    setError(null);
    try {
      await purchaseOrderService.recordPayment(id || '0', paymentData);
      setSuccessMsg(t('purchaseOrder.payment.recordedSuccess'));
      setTimeout(() => setSuccessMsg(null), 4000);
      setShowPaymentForm(false);
      setPaymentData({
        amount: 0,
        paymentDate: new Date().toISOString().slice(0, 10),
        paymentMethod: activePaymentTypes.find((pt) => pt.isDefault && pt.paymentCategory !== 'Credit')?.code
          ?? activePaymentTypes.find((pt) => pt.paymentCategory !== 'Credit')?.code
          ?? '',
        referenceNumber: '',
        notes: '',
      });
      await loadOrder();
      await loadPayments();
    } catch {
      setPaymentError(t('purchaseOrder.payment.failedToRecord'));
    } finally {
      setPaymentSubmitting(false);
    }
  };

  // ── Submit for Approval handler ───────────────────────────────────────────

  const handleSubmitForApproval = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await purchaseOrderService.submitPurchaseOrder(id || '0');
      setSuccessMsg(t('purchaseOrder.submittedSuccess'));
      setTimeout(() => setSuccessMsg(null), 5000);
      await loadOrder();
    } catch (err: unknown) {
      setError(parseApiError(err, t('purchaseOrder.failedToSubmit')).message);
    } finally {
      setSubmitting(false);
    }
  };

  // ── PDF & Email handlers ──────────────────────────────────────────────────

  const handleViewPdf = async () => {
    setPdfLoading(true);
    setError(null);
    try {
      await purchaseOrderService.openPdfInNewTab(id || '0');
    } catch (err: unknown) {
      const msg = parseApiError(err, '').message;
      if (msg === 'popup_blocked') {
        setError(t('purchaseOrder.pdf.popupBlocked'));
      } else {
        setError(`${t('purchaseOrder.pdf.failedToOpen')} (${msg})`);
      }
    } finally {
      setPdfLoading(false);
    }
  };

  const handleSendToSupplier = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await purchaseOrderService.sendToSupplier(id || '0');
      setSuccessMsg(t('purchaseOrder.email.sentSuccess'));
      setTimeout(() => setSuccessMsg(null), 5000);
    } catch {
      setError(t('purchaseOrder.email.failedToSend'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="ims-loading">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!order) {
    return (
      <Layout>
        <div style={{ padding: 32 }}>
          <div className="ims-alert ims-alert-error">{error || t('purchaseOrder.purchaseOrderNotFound')}</div>
          <button className="ims-back-btn" onClick={() => navigate('/purchase-orders')}>
            {t('purchaseOrder.backToPurchaseOrders')}
          </button>
        </div>
      </Layout>
    );
  }

  const langCode = t('common.languageCode') === 'ja' ? 'ja-JP' : 'en-US';
  const fmt = (v: number) =>
    new Intl.NumberFormat(langCode, { style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);

  // Only allow editing Draft orders that have no active cancellation request
  const canEdit =
    order.status === 'Draft' &&
    order.approvalStatus === 'Draft' &&
    order.cancellationStatus === 'None';

  return (
    <Layout>
      <div className="po-detail-page">

        {/* ── Hero ── */}
        <div className="ims-hero">
          <div className="ims-hero-nav">
            <button className="ims-hero-back" onClick={() => navigate('/purchase-orders')}>
              <ArrowLeft size={16} strokeWidth={2} />
              {t('purchaseOrder.backToPurchaseOrders')}
            </button>
          </div>
          <div className="ims-hero-content">
            <div>
              <h1 className="ims-hero-number">{order.poNumber}</h1>
              <p className="ims-hero-supplier">{order.supplierName || '—'}</p>
              <div className="ims-hero-badges">
                <span className={STATUS_BADGE[order.status] ?? 'ims-badge ims-badge-draft'}>
                  {t(STATUS_I18N[order.status] ?? 'purchaseOrder.statusDraft')}
                </span>
                {order.approvalStatus && order.approvalStatus !== 'Draft' && (
                  <span className={APPROVAL_BADGE[order.approvalStatus]}>
                    {t(`purchaseOrder.approval.status${order.approvalStatus}`)}
                  </span>
                )}
                {order.cancellationStatus && order.cancellationStatus !== 'None' && (
                  <span className={CANCELLATION_BADGE[order.cancellationStatus]}>
                    {t(`purchaseOrder.cancellation.status${order.cancellationStatus}`)}
                  </span>
                )}
              </div>
            </div>
            <div className="ims-hero-meta">
              <span className="ims-hero-date">
                {new Date(order.orderDate).toLocaleDateString(langCode)}
              </span>
              {order.expectedDeliveryDate && (
                <span className="ims-hero-date">
                  {t('purchaseOrder.expectedDelivery')}: {new Date(order.expectedDeliveryDate).toLocaleDateString(langCode)}
                </span>
              )}
            </div>
          </div>

          {/* ── Dashboard stat strip ── */}
          <div className="po-hero-stats">
            <div className="po-hero-stat">
              <span className="po-hero-stat-label">{t('purchaseOrder.totalAmount')}</span>
              <span className="po-hero-stat-value">{fmt(order.totalAmount)}</span>
            </div>
            <div className="po-hero-stat">
              <span className="po-hero-stat-label">{t('purchaseOrder.payment.amountPaid')}</span>
              <span className="po-hero-stat-value">{fmt(order.amountPaid)}</span>
            </div>
            <div className="po-hero-stat">
              <span className="po-hero-stat-label">{t('purchaseOrder.payment.balanceDue')}</span>
              <span className="po-hero-stat-value">{fmt(order.balanceDue)}</span>
            </div>
            <div className="po-hero-stat">
              <span className="po-hero-stat-label">{t('purchaseOrder.itemCount')}</span>
              <span className="po-hero-stat-value">{order.lines?.length ?? 0}</span>
            </div>
          </div>
        </div>

        {error && (
          <div className="ims-alert ims-alert-error" style={{ margin: '16px 28px 0' }}>
            {error}
            <button type="button" onClick={() => setError(null)} style={{ marginInlineStart: 'auto', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit' }}>✕</button>
          </div>
        )}

        {successMsg && (
          <div className="ims-alert ims-alert-success" style={{ margin: '16px 28px 0' }}>
            {successMsg}
            <button type="button" onClick={() => setSuccessMsg(null)} style={{ marginInlineStart: 'auto', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit' }}>✕</button>
          </div>
        )}

        {/* My pending approval banner */}
        {approvalStatus && (() => {
          const myPending = approvalStatus.approvals.find(
            a => a.status === 'Pending' && a.assignedToUserId === currentUserId
          );
          return myPending ? (
            <div className="ims-alert" style={{ margin: '16px 28px 0', background: '#dbeafe', border: '1px solid #93c5fd', color: '#1e40af' }}>
              <Info size={16} strokeWidth={2} style={{ flexShrink: 0 }} />
              <span>{t('purchaseOrder.approval.pendingYourApproval', { level: myPending.approvalLevel })}</span>
            </div>
          ) : null;
        })()}

        <div className="ims-detail-body">
          <div className="ims-detail-main">

            {/* Order Details */}
            <div className="ims-card">
              <div className="ims-card-header">
                <h2 className="ims-card-title">{t('purchaseOrder.orderDetails')}</h2>
              </div>
              <div className="ims-card-body">
                <div className="ims-info-grid">
                  <div className="ims-info-field">
                    <span className="ims-info-label">{t('purchaseOrder.poNumber')}</span>
                    <span className="ims-info-value"><code>{order.poNumber}</code></span>
                  </div>
                  <div className="ims-info-field">
                    <span className="ims-info-label">{t('purchaseOrder.supplier')}</span>
                    <span className="ims-info-value">
                      {order.supplierCode && (
                        <> &nbsp;<code style={{ fontSize: 11, marginRight: 4 }}>{order.supplierCode}</code></>
                      )}   
                      {order.supplierName || '—'}
                    </span>
                  </div>
                  <div className="ims-info-field">
                    <span className="ims-info-label">{t('purchaseOrder.orderDate')}</span>
                    <span className="ims-info-value">
                      {new Date(order.orderDate).toLocaleDateString(langCode)}
                    </span>
                  </div>
                  <div className="ims-info-field">
                    <span className="ims-info-label">{t('purchaseOrder.expectedDelivery')}</span>
                    <span className="ims-info-value">
                      {order.expectedDeliveryDate
                        ? new Date(order.expectedDeliveryDate).toLocaleDateString(langCode)
                        : '—'}
                    </span>
                  </div>
                  <div className="ims-info-field">
                    <span className="ims-info-label">{t('purchaseOrder.status')}</span>
                    <span className="ims-info-value">
                      <span className={STATUS_BADGE[order.status] ?? 'ims-badge ims-badge-draft'}>
                        {t(STATUS_I18N[order.status] ?? 'purchaseOrder.statusDraft')}
                      </span>
                    </span>
                  </div>
                  <div className="ims-info-field">
                    <span className="ims-info-label">{t('purchaseOrder.totalAmount')}</span>
                    <span className="ims-info-value po-amount-value">
                      {fmt(order.totalAmount)}
                    </span>
                  </div>
                  {order.paymentTypeName && (
                    <div className="ims-info-field">
                      <span className="ims-info-label">{t('purchaseOrder.paymentType', 'Payment Type')}</span>
                      <span className="ims-info-value">{order.paymentTypeName}</span>
                    </div>
                  )}
                  {order.notes && (
                    <div className="ims-info-field" style={{ gridColumn: '1 / -1' }}>
                      <span className="ims-info-label">{t('purchaseOrder.notes')}</span>
                      <span className="ims-info-value">{order.notes}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ── Approval Progress Stepper ── */}
            {order.status !== 'Draft' && (
              <div className="ims-card">
                <div className="ims-card-header">
                  <h2 className="ims-card-title">
                    {t('purchaseOrder.approval.approvalProgress')}
                  </h2>
                  <span className={APPROVAL_BADGE[order.approvalStatus]}>
                    {t(`purchaseOrder.approval.status${order.approvalStatus}`)}
                  </span>
                </div>
                <div className="ims-card-body">
                  {order.totalApprovalLevels === 0 ? (
                    /* Auto-approved — no rules configured */
                    <div className="po-auto-approved">
                      <Check size={16} strokeWidth={2.5} />
                      {t('purchaseOrder.approval.noApprovalRequired')}
                    </div>
                  ) : (
                    /* Horizontal step progress */
                    <div className="po-stepper">
                      {Array.from({ length: order.totalApprovalLevels }, (_, i) => {
                        const level = i + 1;
                        const rec = approvalStatus?.approvals.find(
                          (a) => a.approvalLevel === level,
                        );
                        const isApproved = rec?.status === 'Approved';
                        const isRejected = rec?.status === 'Rejected';
                        const isPending  = rec?.status === 'Pending';

                        const circleClass = isApproved ? 'po-stepper-circle--approved'
                          : isRejected ? 'po-stepper-circle--rejected'
                          : isPending  ? 'po-stepper-circle--pending'
                          : 'po-stepper-circle--idle';

                        const statusClass = isApproved ? 'po-stepper-status--approved'
                          : isRejected ? 'po-stepper-status--rejected'
                          : isPending  ? 'po-stepper-status--pending'
                          : 'po-stepper-status--idle';

                        return (
                          <React.Fragment key={level}>
                            {/* Step node */}
                            <div className="po-stepper-node">
                              {/* Circle */}
                              <div className={`po-stepper-circle ${circleClass}`}>
                                {isApproved ? (
                                  <Check size={18} strokeWidth={3} />
                                ) : isRejected ? (
                                  <X size={18} strokeWidth={3} />
                                ) : level}
                              </div>

                              {/* "Level N" label */}
                              <div className="po-stepper-label">
                                {t('purchaseOrder.approval.level')} {level}
                              </div>

                              {/* Approver name */}
                              {rec?.assignedToUserName && (
                                <div className="po-stepper-name">
                                  {rec.assignedToUserName}
                                </div>
                              )}

                              {/* Status label */}
                              <div className={`po-stepper-status ${statusClass}`}>
                                {isApproved
                                  ? t('purchaseOrder.approval.recordApproved')
                                  : isRejected
                                  ? t('purchaseOrder.approval.recordRejected')
                                  : isPending
                                  ? t('purchaseOrder.approval.recordPending')
                                  : '—'}
                              </div>
                            </div>

                            {/* Connector between steps */}
                            {level < order.totalApprovalLevels && (
                              <div className={`po-stepper-connector ${isApproved ? 'po-stepper-connector--approved' : 'po-stepper-connector--idle'}`} />
                            )}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Line Items */}
            {order.lines && order.lines.length > 0 && (
              <div className="ims-card">
                <div className="ims-card-header">
                  <h2 className="ims-card-title">{t('purchaseOrder.lineItems')}</h2>
                </div>
                <div className="ims-card-body" style={{ padding: 0 }}>
                  <div className="ims-items-scroll">
                    <table className="ims-items-table">
                      <thead>
                        <tr>
                          <th className="ims-items-th">
                            {t('purchaseOrder.product')}
                          </th>
                          <th className="ims-items-th ims-items-th--right">
                            {t('purchaseOrder.quantity')}
                          </th>
                          <th className="ims-items-th ims-items-th--right">
                            {t('purchaseOrder.unitPrice')}
                          </th>
                          <th className="ims-items-th ims-items-th--right">
                            {t('purchaseOrder.lineTotal')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {order.lines.map((item, idx) => (
                          <tr key={idx} className="ims-items-tr">
                            <td className="ims-items-td">{item.productName}</td>
                            <td className="ims-items-td ims-items-td--right">{item.quantity}</td>
                            <td className="ims-items-td ims-items-td--right">{fmt(item.unitPrice)}</td>
                            <td className="ims-items-td ims-items-td--right" style={{ fontWeight: 500 }}>
                              {fmt(item.quantity * item.unitPrice)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="po-tfoot-row">
                          <td colSpan={3} className="ims-items-td ims-items-td--right po-tfoot-label">
                            {t('purchaseOrder.totalAmount')}
                          </td>
                          <td className="ims-items-td ims-items-td--right po-tfoot-total">
                            {fmt(order.totalAmount)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Approval Status Panel — shown when PO is submitted */}
            {approvalStatus && order.status !== 'Draft' && (
              <ApprovalPanel
                approvalStatus={approvalStatus}
                currentUserId={currentUserId}
                onApprove={(approvalId) => { setApprovingApprovalId(approvalId); setShowApproveModal(true); }}
                onReject={(approvalId) => { setRejectingApprovalId(approvalId); setShowRejectModal(true); }}
                busy={submitting}
                t={t}
              />
            )}

            {/* Payment History Panel */}
            {(payments.length > 0 || (order.amountPaid ?? 0) > 0) && (
              <div className="ims-card">
                <div className="ims-card-header">
                  <h2 className="ims-card-title">{t('purchaseOrder.payment.history')}</h2>
                  {payments.length > 0 && (
                    <span style={{ fontSize: 12, color: 'var(--ims-text-muted)' }}>({payments.length})</span>
                  )}
                </div>
                {paymentsLoading ? (
                  <div className="ims-card-body" style={{ color: 'var(--ims-text-muted)', fontSize: 13 }}>{t('common.loading')}</div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table className="ims-items-table">
                      <thead>
                        <tr>
                          <th className="ims-items-th">{t('purchaseOrder.payment.date')}</th>
                          <th className="ims-items-th">{t('purchaseOrder.payment.method')}</th>
                          <th className="ims-items-th">{t('purchaseOrder.payment.reference')}</th>
                          <th className="ims-items-th ims-items-th--right">{t('purchaseOrder.payment.amount')}</th>
                          <th className="ims-items-th">{t('purchaseOrder.payment.recordedBy')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payments.map((p) => (
                          <tr key={p.id} className="ims-items-tr">
                            <td className="ims-items-td">{new Date(p.paymentDate).toLocaleDateString(langCode)}</td>
                            <td className="ims-items-td">
                              <span className="si-payment-method-badge">{t(`purchaseOrder.payment.method${p.paymentMethod}`) || p.paymentMethod}</span>
                            </td>
                            <td className="ims-items-td" style={{ fontSize: 12, color: 'var(--ims-text-muted)' }}>{p.referenceNumber || '—'}</td>
                            <td className="ims-items-td ims-items-td--right" style={{ fontWeight: 600, color: 'var(--ims-green-text)' }}>{fmt(p.amount)}</td>
                            <td className="ims-items-td" style={{ fontSize: 12, color: 'var(--ims-text-muted)' }}>{p.recordedByUserName || `#${p.recordedByUserId}`}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="po-tfoot-row">
                          <td colSpan={3} className="ims-items-td ims-items-td--right po-tfoot-label">{t('purchaseOrder.payment.totalPaid')}</td>
                          <td className="ims-items-td ims-items-td--right po-tfoot-total">{fmt(payments.reduce((s, p) => s + p.amount, 0))}</td>
                          <td />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Cancellation Panel — shown for cancellable orders and orders with any cancellation activity */}
            {(CANCELLABLE_STATUSES.includes(order.status) || order.cancellationStatus !== 'None') && (
              <CancellationPanel
                order={order}
                cancellation={cancellation}
                currentUserId={currentUserId}
                submitting={submitting}
                onRequest={handleRequestCancellation}
                onWithdraw={handleWithdrawCancellation}
                onApprove={handleApproveCancellation}
                onReject={handleRejectCancellation}
                t={t}
              />
            )}

            {/* Audit Info */}
            <div className="ims-card">
              <div className="ims-card-header">
                <h2 className="ims-card-title">{t('purchaseOrder.auditInformation')}</h2>
              </div>
              <div className="ims-card-body">
                <div className="ims-info-grid">
                  <div className="ims-info-field">
                    <span className="ims-info-label">{t('purchaseOrder.purchaseOrderId')}</span>
                    <span className="ims-info-value">{order.id}</span>
                  </div>
                  <div className="ims-info-field">
                    <span className="ims-info-label">{t('purchaseOrder.createdDate')}</span>
                    <span className="ims-info-value">
                      {new Date(order.createdAt).toLocaleDateString(langCode)}{' '}
                      {new Date(order.createdAt).toLocaleTimeString(langCode)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* Sidebar */}
          <div className="ims-detail-sidebar">
            <div className="ims-card">
              <div className="ims-card-header">
                <h3 className="ims-card-title">{t('common.actions')}</h3>
              </div>
              <div className="ims-card-body">
                <div className="ims-actions-list">
                  {/* Submit for Approval — only for Draft orders that haven't been submitted yet */}
                  {order.status === 'Draft' && order.approvalStatus === 'Draft' && (
                    <button
                      className="ims-action-btn ims-action-btn--approve"
                      disabled={submitting}
                      onClick={handleSubmitForApproval}
                      title={t('purchaseOrder.submitTooltip')}
                    >
                      {submitting ? (
                        <span className="ims-btn-spinner" role="status" />
                      ) : (
                        <SendHorizontal size={16} strokeWidth={2} />
                      )}
                      {submitting ? t('purchaseOrder.submitting') : t('purchaseOrder.submitForApproval')}
                    </button>
                  )}
                  {canEdit && (
                    <button
                      className="ims-action-btn ims-action-btn--edit"
                      onClick={() => navigate(`/purchase-orders/${order.id}/edit`)}
                    >
                      <PenLine size={16} strokeWidth={2} />
                      {t('purchaseOrder.editPurchaseOrder')}
                    </button>
                  )}
                  {canEdit && (
                    <button
                      className="ims-action-btn ims-action-btn--reject"
                      onClick={() => navigate(`/purchase-orders/${order.id}/delete`)}
                    >
                      <Trash2 size={16} strokeWidth={2} />
                      {t('purchaseOrder.deletePurchaseOrder')}
                    </button>
                  )}
                  {/* View PDF — available for all orders */}
                  <button
                    className="ims-action-btn ims-action-btn--back"
                    disabled={pdfLoading || submitting}
                    onClick={handleViewPdf}
                    title={t('purchaseOrder.pdf.viewTooltip')}
                  >
                    {pdfLoading ? (
                      <span className="ims-btn-spinner" role="status" />
                    ) : (
                      <FileText size={16} strokeWidth={2} />
                    )}
                    {pdfLoading ? t('purchaseOrder.pdf.loading') : t('purchaseOrder.pdf.viewPdf')}
                  </button>

                  {/* Send to Supplier — only when fully approved */}
                  {order.approvalStatus === 'Approved' && (
                    <button
                      className="ims-action-btn ims-action-btn--back"
                      disabled={submitting}
                      onClick={handleSendToSupplier}
                      title={t('purchaseOrder.email.sendTooltip')}
                    >
                      <SendHorizontal size={16} strokeWidth={2} />
                      {submitting ? (
                        <span className="ims-btn-spinner" role="status" />
                      ) : null}
                      {t('purchaseOrder.email.sendToSupplier')}
                    </button>
                  )}

                  <button
                    className="ims-action-btn ims-action-btn--back"
                    onClick={() => navigate('/purchase-orders')}
                  >
                    <ArrowLeft size={16} strokeWidth={2} />
                    {t('purchaseOrder.backToPurchaseOrders')}
                  </button>
                </div>
              </div>
            </div>

            <div className="ims-card">
              <div className="ims-card-header">
                <h3 className="ims-card-title">{t('purchaseOrder.orderSummary')}</h3>
              </div>
              <div className="ims-card-body">
                <div className="ims-info-field" style={{ marginBottom: 8 }}>
                  <span className="ims-info-label">{t('purchaseOrder.itemCount')}</span>
                  <span className="ims-info-value">{order.lines?.length ?? '—'}</span>
                </div>
                <div className="ims-info-field">
                  <span className="ims-info-label">{t('purchaseOrder.totalAmount')}</span>
                  <span className="ims-info-value po-amount-value">
                    {fmt(order.totalAmount)}
                  </span>
                </div>
                {order.totalApprovalLevels > 0 && (
                  <div className="ims-info-field" style={{ marginTop: 8 }}>
                    <span className="ims-info-label">{t('purchaseOrder.approval.approvalLevels')}</span>
                    <span className="ims-info-value">{order.currentApprovalLevel} / {order.totalApprovalLevels}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Payment Summary Card */}
            <div className="ims-card">
              <div className="ims-card-header">
                <h3 className="ims-card-title">{t('purchaseOrder.payment.summary')}</h3>
              </div>
              <div className="ims-card-body">
                <div className="si-payment-summary">
                  <div className="si-payment-row">
                    <span>{t('purchaseOrder.totalAmount')}</span>
                    <strong>{fmt(order.totalAmount)}</strong>
                  </div>
                  <div className="si-payment-row si-payment-row--paid">
                    <span>{t('purchaseOrder.payment.amountPaid')}</span>
                    <strong>{fmt(order.amountPaid ?? 0)}</strong>
                  </div>
                  <div className={`si-payment-row si-payment-row--total ${(order.balanceDue ?? order.totalAmount) <= 0 ? 'si-payment-row--balance-zero' : 'si-payment-row--balance'}`}>
                    <span>{t('purchaseOrder.payment.balanceDue')}</span>
                    <strong>{fmt(order.balanceDue ?? order.totalAmount)}</strong>
                  </div>
                </div>

                {/* Record Payment Form */}
                {order.approvalStatus === 'Approved' && (order.balanceDue ?? order.totalAmount) > 0 && (
                  <div style={{ marginTop: 16 }}>
                    {!showPaymentForm ? (
                      <button
                        className="ims-btn ims-btn-success"
                        style={{ width: '100%' }}
                        onClick={() => setShowPaymentForm(true)}
                        disabled={paymentSubmitting}
                      >
                        <CreditCard size={16} strokeWidth={2} />
                        {t('purchaseOrder.payment.recordPayment')}
                      </button>
                    ) : (
                      <div className="si-payment-form">
                        <p className="si-payment-form-title">{t('purchaseOrder.payment.recordPayment')}</p>
                        <div className="ims-form-group">
                          <label className="ims-form-label">
                            {t('purchaseOrder.payment.amount')} <span className="required">*</span>
                          </label>
                          <input
                            type="number"
                            className="ims-form-control"
                            value={paymentData.amount || ''}
                            min={0.01}
                            step="0.01"
                            onChange={(e) => setPaymentData((p) => ({ ...p, amount: parseFloat(e.target.value) || 0 }))}
                          />
                          {paymentError && <span style={{ color: 'var(--color-danger)', fontSize: 12 }}>{paymentError}</span>}
                        </div>
                        <div className="ims-form-group">
                          <label className="ims-form-label">{t('purchaseOrder.payment.date')}</label>
                          <input
                            type="date"
                            className="ims-form-control"
                            value={paymentData.paymentDate}
                            onChange={(e) => setPaymentData((p) => ({ ...p, paymentDate: e.target.value }))}
                          />
                        </div>
                        <div className="ims-form-group">
                          <label className="ims-form-label">{t('purchaseOrder.payment.method')}</label>
                          <select
                            className="ims-form-control"
                            value={paymentData.paymentMethod}
                            onChange={(e) => setPaymentData((p) => ({ ...p, paymentMethod: e.target.value as 'Cash' | 'BankTransfer' | 'CreditCard' | 'Cheque' }))}
                          >
                            {activePaymentTypes.map((pt) => (
                              <option key={pt.id} value={pt.code}>{pt.name}</option>
                            ))}
                          </select>
                        </div>
                        <div className="ims-form-group">
                          <label className="ims-form-label">{t('purchaseOrder.payment.reference')}</label>
                          <input
                            type="text"
                            className="ims-form-control"
                            placeholder={t('purchaseOrder.payment.referencePlaceholder')}
                            value={paymentData.referenceNumber}
                            onChange={(e) => setPaymentData((p) => ({ ...p, referenceNumber: e.target.value }))}
                          />
                        </div>
                        <div className="si-payment-form-footer">
                          <button
                            className="btn-cancel"
                            onClick={() => { setShowPaymentForm(false); setPaymentError(null); }}
                            disabled={paymentSubmitting}
                          >
                            {t('common.cancel')}
                          </button>
                          <button
                            className="ims-btn ims-btn-success"
                            onClick={handleRecordPayment}
                            disabled={paymentSubmitting}
                          >
                            {paymentSubmitting ? <span className="ims-btn-spinner" /> : null}
                            {t('purchaseOrder.payment.save')}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* ── Approve with comments modal ── */}
      {showApproveModal && (
        <div className="ims-modal-overlay">
          <div className="ims-modal">
            <h3 className="ims-modal-title">{t('purchaseOrder.approval.approveTitle')}</h3>
            <p className="ims-modal-sub">{t('purchaseOrder.approval.approvalCommentsOptional')}</p>
            <textarea
              value={approvalComments}
              onChange={(e) => setApprovalComments(e.target.value)}
              placeholder={t('purchaseOrder.approval.approvalCommentsPlaceholder')}
              className="ims-form-control ims-textarea"
              rows={4}
            />
            <div className="ims-modal-footer">
              <button
                type="button"
                className="btn-cancel"
                disabled={submitting}
                onClick={() => { setShowApproveModal(false); setApprovalComments(''); setApprovingApprovalId(null); }}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="ims-btn ims-btn-success"
                disabled={submitting}
                onClick={() => approvingApprovalId && handleApprove(approvingApprovalId)}
              >
                {submitting && <span className="ims-btn-spinner" />}
                {t('purchaseOrder.approval.approvePO')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Reject modal ── */}
      {showRejectModal && (
        <div className="ims-modal-overlay">
          <div className="ims-modal">
            <h3 className="ims-modal-title">{t('purchaseOrder.approval.rejectTitle')}</h3>
            <p className="ims-modal-sub">{t('purchaseOrder.approval.rejectionReasonRequired')}</p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder={t('purchaseOrder.approval.rejectionReasonPlaceholder')}
              className="ims-form-control ims-textarea"
              rows={5}
            />
            <div className="ims-modal-footer">
              <button
                type="button"
                className="btn-cancel"
                disabled={submitting}
                onClick={() => { setShowRejectModal(false); setRejectionReason(''); setRejectingApprovalId(null); }}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="btn-delete"
                disabled={submitting}
                onClick={handleReject}
              >
                {submitting && <span className="ims-btn-spinner" />}
                {t('purchaseOrder.approval.rejectPO')}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default PurchaseOrderDetailPage;
